import { useEffect, useState } from "react";
import {
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { ResponsiveTable } from "@/components/ResponsiveTable";
import { StatusChip } from "@/components/StatusChip";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { formatDateTime as formatDate } from "@/lib/datetime";

export default function AdminsPage({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [seedEmail, setSeedEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [editForm, setEditForm] = useState({ email: "", name: "" });
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isSeedActor = (user?.email || "").toLowerCase() === (seedEmail || "").toLowerCase();
  // Equal rights — any super admin can manage other admins.
  const canManage = true;

  const load = async () => {
    try {
      const { data } = await api.get("/admin/admins");
      setItems(data.items || []);
      setSeedEmail(data.seed_email || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setForm({ email: "", name: "", password: "" });
    setCreateOpen(true);
  };
  const submitCreate = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error("All fields are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/admins", form);
      toast.success(`Admin ${form.email} created`);
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (admin) => {
    setEditing(admin);
    setEditForm({ email: admin.email, name: admin.name || "" });
  };
  const submitEdit = async () => {
    setSubmitting(true);
    try {
      const payload = {};
      if (editForm.name !== (editing.name || "")) payload.name = editForm.name;
      if (editForm.email !== editing.email) payload.email = editForm.email;
      if (Object.keys(payload).length === 0) {
        setEditing(null);
        return;
      }
      await api.patch(`/admin/admins/${editing.id}`, payload);
      toast.success("Admin updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const openReset = (admin) => {
    setResetting(admin);
    setNewPassword("");
  };
  const submitReset = async () => {
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/admins/${resetting.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetting.email}. Share it with them out-of-band.`);
      setResetting(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/admin/admins/${deleteConfirm.id}`);
      toast.success(`Admin ${deleteConfirm.email} deleted`);
      setDeleteConfirm(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const newAdminButton = canManage && (
    <button
      data-testid="admins-new-button"
      onClick={openCreate}
      className="inline-flex items-center gap-1.5 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors"
    >
      <Plus size={14} /> New admin
    </button>
  );

  const tableColumns = [
    {
      key: "email",
      label: "Email",
      mobileVariant: "title",
      render: (a) => (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="rt-primary break-all">{a.email}</span>
          {a.is_seed && (
            <span
              data-testid="admin-seed-badge"
              className="text-xs font-medium px-2 py-0.5 rounded-full border border-[#3B82F6]/20 bg-[#3B82F6]/10 text-[#1D4ED8]"
            >
              Primary
            </span>
          )}
          {!a.is_seed && a.id === user?.id && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full border border-[#E5E7EB] bg-[#F8FAFC] text-[#64748B]">
              You
            </span>
          )}
        </div>
      ),
    },
    { key: "name", label: "Name", render: (a) => <span className="text-[#334155]">{a.name || "—"}</span> },
    {
      key: "status",
      label: "Status",
      render: (a) => <StatusChip testId={`admin-status-chip-${a.status || "active"}`} status={a.status || "active"} />,
    },
    {
      key: "last_login_at",
      label: "Last login",
      render: (a) => <span className="text-xs text-[#64748B]">{formatDate(a.last_login_at)}</span>,
    },
    {
      key: "created_at",
      label: "Created",
      render: (a) => <span className="text-xs text-[#64748B]">{formatDate(a.created_at)}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (a) =>
        canManage && !a.is_seed ? (
          <div className="flex items-center justify-end gap-1.5" data-testid={`admin-row-actions-${a.id}`}>
            <button
              onClick={() => openEdit(a)}
              data-testid={`admin-edit-${a.id}`}
              title="Edit details"
              aria-label="Edit details"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#0F172A] hover:border-[#CBD5E1]"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => openReset(a)}
              data-testid={`admin-reset-${a.id}`}
              title="Reset password"
              aria-label="Reset password"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#0F172A] hover:border-[#CBD5E1]"
            >
              <KeyRound size={13} />
            </button>
            <button
              onClick={() => setDeleteConfirm(a)}
              data-testid={`admin-delete-${a.id}`}
              title="Delete admin"
              aria-label="Delete admin"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#B91C1C] hover:bg-[#FEF2F2] hover:border-[#FCA5A5]"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ) : (
          <span className="text-[#94A3B8] text-xs">—</span>
        ),
    },
  ];

  return (
    <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10"} data-testid="admins-page">
      {embedded ? (
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h2 className="font-['Geist'] font-semibold text-xl text-[#0F172A]">Super Admins</h2>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
              Add, edit and moderate the people who can manage Probe customers. Every super admin has equal rights.
            </p>
          </div>
          {newAdminButton}
        </div>
      ) : (
        <PageHeader
          title="Super Admins"
          description="Add, edit and moderate the people who can manage Probe customers. Every super admin has equal rights."
          actions={newAdminButton}
        />
      )}

      {loading ? (
        <div className="surface-card p-6 text-sm text-[#64748B] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading admins…
        </div>
      ) : (
        <ResponsiveTable testId="admins-table" columns={tableColumns} rows={items} />
      )}

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-xl border-[#E5E7EB]" data-testid="admin-create-dialog">
          <DialogHeader>
            <DialogTitle>Invite a new admin</DialogTitle>
            <DialogDescription>
              They&apos;ll get super-admin privileges. Share the temporary password with them out-of-band.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1.5">Email</label>
              <Input
                data-testid="admin-create-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1.5">Name</label>
              <Input
                data-testid="admin-create-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1.5">Temporary password</label>
              <Input
                data-testid="admin-create-password"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] font-mono"
                autoComplete="off"
              />
              <p className="text-xs text-[#64748B] mt-1.5">Minimum 8 characters.</p>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setCreateOpen(false)}
              className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]"
            >
              Cancel
            </button>
            <button
              data-testid="admin-create-submit"
              onClick={submitCreate}
              disabled={submitting}
              className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {submitting && <Loader2 size={13} className="animate-spin" />}
              Create admin
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-xl border-[#E5E7EB]" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit admin</DialogTitle>
            <DialogDescription>Update the admin&apos;s name or email.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-[#475569] block mb-1.5">Email</label>
                <Input
                  data-testid="admin-edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#475569] block mb-1.5">Name</label>
                <Input
                  data-testid="admin-edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setEditing(null)}
              className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]"
            >
              Cancel
            </button>
            <button
              data-testid="admin-edit-submit"
              onClick={submitEdit}
              disabled={submitting}
              className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {submitting && <Loader2 size={13} className="animate-spin" />}
              Save changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent className="rounded-xl border-[#E5E7EB]" data-testid="admin-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new temporary password for <span className="font-mono">{resetting?.email}</span>. Their existing sessions will continue to work until they sign out.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-[#475569] block mb-1.5">New password</label>
            <Input
              data-testid="admin-reset-password"
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] font-mono"
              autoComplete="new-password"
            />
            <p className="text-xs text-[#64748B] mt-1.5">Minimum 8 characters.</p>
          </div>
          <DialogFooter>
            <button
              onClick={() => setResetting(null)}
              className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]"
            >
              Cancel
            </button>
            <button
              data-testid="admin-reset-submit"
              onClick={submitReset}
              disabled={submitting}
              className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {submitting && <Loader2 size={13} className="animate-spin" />}
              Reset password
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent className="rounded-xl border-[#E5E7EB]" data-testid="admin-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this admin?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-mono">{deleteConfirm?.email}</span> from Probe. They&apos;ll lose access immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-md border border-[#E5E7EB]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm-submit"
              onClick={submitDelete}
              className="rounded-md bg-[#EF4444] text-white hover:bg-[#DC2626] inline-flex items-center gap-1.5"
            >
              {submitting && <Loader2 size={13} className="animate-spin" />}
              Delete admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
